import { Rocket, RotateCcw } from 'lucide-react';
import type { LaunchBooster } from '@vtak/mod-space/client';

/** A recovery outcome label and Tailwind color classes. */
interface RecoveryStatus {
  label: string;
  classes: string;
}

/** Booster + recovery details for a launch (one card per stage). */
export function LaunchBoosters({ boosters }: { boosters: LaunchBooster[] }): React.JSX.Element | null {
  if (boosters.length === 0) return null;
  return (
    <div className="border-t border-echo-border px-4 py-3">
      <div className="mb-2 text-[11px] uppercase tracking-wider text-muted-foreground">Booster &amp; Recovery</div>
      <div className="space-y-2">
        {boosters.map((booster) => (
          <BoosterCard key={booster.serial} booster={booster} />
        ))}
      </div>
    </div>
  );
}

/** A single booster card. */
function BoosterCard({ booster }: { booster: LaunchBooster }): React.JSX.Element {
  const recovery = recoveryStatus(booster);
  return (
    <div className="rounded-md border border-echo-border/60 bg-white/[0.03] p-2.5">
      <div className="flex items-center gap-2">
        <Rocket className="h-3.5 w-3.5 shrink-0 text-echo-yellow" />
        <span className="text-sm font-medium text-foreground">{booster.serial}</span>
        {booster.flightNumber > 0 && (
          <span className="text-[11px] text-muted-foreground">Flight #{booster.flightNumber}</span>
        )}
        {booster.reused && (
          <span className="flex items-center gap-1 rounded-full bg-echo-yellow/15 px-2 py-0.5 text-[10px] font-semibold text-echo-yellow">
            <RotateCcw className="h-3 w-3" />
            Reused
          </span>
        )}
        <span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-semibold ${recovery.classes}`}>
          {recovery.label}
        </span>
      </div>
      {booster.landingAttempt && booster.landingLocation && (
        <div className="mt-1.5 truncate text-[11px] text-muted-foreground">
          {[booster.landingType, booster.landingLocation].filter(Boolean).join('  ·  ')}
        </div>
      )}
    </div>
  );
}

/** Derive a recovery label + color from a booster's landing fields. */
function recoveryStatus(booster: LaunchBooster): RecoveryStatus {
  if (!booster.landingAttempt) return { label: 'Expended', classes: 'bg-white/10 text-muted-foreground' };
  if (booster.landingSuccess === true) return { label: 'Recovered', classes: 'bg-green-500/20 text-green-400' };
  if (booster.landingSuccess === false) return { label: 'Lost', classes: 'bg-red-500/20 text-red-400' };
  return { label: 'Recovery planned', classes: 'bg-echo-yellow/15 text-echo-yellow' };
}
